import { Button } from '../ui/button';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/v1`;
const isoIn = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
const CI = isoIn(30);
const CO = isoIn(32);

const ENDPOINTS = [
  { method: 'GET', path: '/api/public/availability?check_in=YYYY-MM-DD&check_out=YYYY-MM-DD', desc: 'GoNomikai site: units free per website category (rooms, or beds for dorms). Uses rooms linked via Rooms → Website category, otherwise the configured inventory.' },
  { method: 'POST', path: '/api/public/bookings', desc: 'GoNomikai site: ingest type "room" | "experience" | "waitlist". Idempotent on booking_id / waitlist_id. Returns { ok, pms_reservation_id, booking_id }. Website totals are stored as GST-inclusive.' },
  { method: 'GET', path: '/rooms', desc: 'List all bookable rooms (and dorm beds) with base rates.' },
  { method: 'GET', path: '/availability?check_in=YYYY-MM-DD&check_out=YYYY-MM-DD&guests=2&meal_plan=breakfast', desc: 'Rooms free for the dates, with a full price quote (rate, meals, GST, total). Dorm quotes are per bed.' },
  { method: 'POST', path: '/bookings', desc: 'Create a confirmed booking. The guest is matched by email or created automatically. Dormitory: one booking per bed (num_guests = 1; bed auto-assigned if bed_id is omitted).' },
];

const SAMPLES = {
  'cURL: check availability': `curl "${BASE}/availability?check_in=${CI}&check_out=${CO}&guests=2" \\
  -H "X-API-Key: YOUR_API_KEY"`,
  'cURL: create booking': `curl -X POST "${BASE}/bookings" \\
  -H "X-API-Key: YOUR_API_KEY" -H "Content-Type: application/json" \\
  -d '{
    "guest": { "name": "Asha Rai", "email": "asha@example.com", "phone": "+919800000000", "nationality": "Indian" },
    "room_id": "ROOM_ID_FROM_AVAILABILITY",
    "bed_id": null,
    "check_in_date": "${CI}",
    "check_out_date": "${CO}",
    "num_guests": 2,
    "meal_plan": "breakfast",
    "special_requests": "Late arrival",
    "coupon_code": null
  }'`,
  'Python (website backend)': `import os, requests

NOMI = "${BASE}"
HEADERS = {"X-API-Key": os.environ["NOMI_CONNECT_API_KEY"]}

avail = requests.get(f"{NOMI}/availability", headers=HEADERS,
    params={"check_in": "${CI}", "check_out": "${CO}", "guests": 2}).json()

booking = requests.post(f"{NOMI}/bookings", headers=HEADERS, json={
    "guest": {"name": "Asha Rai", "email": "asha@example.com", "phone": "+919800000000"},
    "room_id": avail["available_rooms"][0]["id"],
    "check_in_date": "${CI}", "check_out_date": "${CO}",
    "num_guests": 2, "meal_plan": "breakfast",
}).json()
print(booking["booking_number"], booking["pricing"]["total_amount"])`,
};

const Code = ({ title, code }) => (
  <div className="rounded-md overflow-hidden border border-stone-800" data-testid={`code-sample-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <div className="flex items-center justify-between bg-stone-800 px-3 py-1.5">
      <span className="text-xs text-stone-300">{title}</span>
      <Button size="sm" variant="ghost" className="h-7 text-stone-300 hover:bg-stone-700" onClick={() => { navigator.clipboard.writeText(code); toast.success('Copied'); }}><Copy className="w-3.5 h-3.5" /></Button>
    </div>
    <pre className="bg-stone-900 text-sage-100 text-xs p-4 overflow-x-auto"><code>{code}</code></pre>
  </div>
);

export const DeveloperDocsTab = () => (
  <div className="space-y-5 max-w-4xl" data-testid="developer-docs">
    <div className="bg-white border border-stone-200 rounded-md p-5 space-y-3">
      <p className="text-sm text-stone-600">Base URL</p>
      <p className="font-mono text-sm bg-sage-50 text-sage-800 px-3 py-2 rounded border border-sage-100 break-all" data-testid="api-base-url">{BASE}</p>
      <p className="text-sm text-stone-600">Send your key in the <code className="bg-stone-100 px-1 rounded">X-API-Key</code> header (the GoNomikai endpoints also accept <code className="bg-stone-100 px-1 rounded">Authorization: Bearer</code>). Call these endpoints from your website's <b>backend</b>, so the key stays secret. Bookings show up here with source "Website".</p>
      <p className="text-xs text-stone-500">Meal plans: room_only · breakfast · breakfast_lunch · breakfast_dinner · all_meals. Errors: 400 invalid input, 401 bad key, 404 room not found, 409 dates already booked.</p>
    </div>
    <div className="bg-white border border-stone-200 rounded-md divide-y divide-stone-100">
      {ENDPOINTS.map((e) => (
        <div key={e.path} className="p-4 flex gap-3">
          <span className={`text-xs font-bold px-2 py-0.5 rounded h-fit ${e.method === 'GET' ? 'bg-sage-100 text-sage-800' : 'bg-stone-800 text-white'}`}>{e.method}</span>
          <div><p className="font-mono text-sm text-stone-800 break-all">{e.path}</p><p className="text-sm text-stone-500 mt-0.5">{e.desc}</p></div>
        </div>
      ))}
    </div>
    {Object.entries(SAMPLES).map(([t, c]) => <Code key={t} title={t} code={c} />)}
  </div>
);
